import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Menu from "./Menu";
import SortDropdown from "./SortDropdown";
import StudentInfo from "./StudentInfo";
import useAppSelector from "hooks/useAppSelector";
import useAllClasses from "hooks/useAllClasses";
import useAppDispatch from "hooks/useAppDispatch";
import {
  getStudents,
  getStudentsBeenInClass,
} from "features/student/studentAction";
import { setStudents } from "features/student/studentSlice";
import Container from "components/__reusable/Container";
import { Student } from "types/Student";
import { formatDate } from "utilities/DateFormatter";
import { SupportedLang } from "config/constants";
import LoadingDialog from "components/__reusable/LoadingDialog";
import { getTrainingPictures } from "services/trainingPictureServices";
import { getCurrentSchoolYear } from "utilities/CurrentSchoolYear";
import { isStudentGraduated as isStudentGraduatedUtil } from "utilities/StudentStatus";

interface TrainingPictureCounts {
  [studentId: number]: number;
}

interface PlanDisplayConfig {
  text: string;
  color: string;
}

const PLAN_COLORS = {
  FREE_TRIAL: "#DC747E",
  FREE: "#E1BC58",
  PREMIUM: "#507ABB",
  FAMILY: "#DC747E",
} as const;

const PLAN_CONFIGS: Record<string, PlanDisplayConfig> = {
  free_trial: {
    text: "ファミリー（無料体験中）",
    color: PLAN_COLORS.FREE_TRIAL,
  },
  Free: { text: "フリー", color: PLAN_COLORS.FREE },
  Premium: { text: "プレミアム", color: PLAN_COLORS.PREMIUM },
  Family: { text: "ファミリー", color: PLAN_COLORS.FAMILY },
};

const ERROR_INDICATOR = (
  <span style={{ color: "red", fontWeight: "bold" }} aria-label="Error">
    !
  </span>
);
const SUCCESS_INDICATOR = <span aria-label="Success">○</span>;
const DELIVERY_NG_INDICATOR = (
  <span style={{ fontWeight: "bold" }} aria-label="Delivery NG">
    ※
  </span>
);

const StudentTable = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { students, sortBy, loading } = useAppSelector(
    (state) => state.student,
  );
  const { classes, isLoading: isLoadingClasses } = useAllClasses();
  const { facility } = useAppSelector((state) => state.facility);
  const dispatch = useAppDispatch();

  const [trainingPictureCounts, setTrainingPictureCounts] =
    useState<TrainingPictureCounts>({});
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);
  const [countsFetchError, setCountsFetchError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const trainingPictureCountsRef = useRef<TrainingPictureCounts>({});
  const classesRef = useRef(classes);

  useEffect(() => {
    classesRef.current = classes;
  }, [classes]);

  const classId = useMemo(() => {
    const parsedId = parseInt(id || "", 10);
    return isNaN(parsedId) ? null : parsedId;
  }, [id]);

  const hasValidClassId = useMemo(() => classId !== null, [classId]);

  useEffect(() => {
    const preventGoogleTranslate = () => {
      const existingMeta = document.querySelector('meta[name="google"]');
      if (!existingMeta) {
        const meta = document.createElement("meta");
        meta.name = "google";
        meta.content = "notranslate";
        document.head.appendChild(meta);
      }

      if (containerRef.current) {
        containerRef.current.setAttribute("translate", "no");
        containerRef.current.classList.add("notranslate");
      }
    };

    preventGoogleTranslate();

    const handleMutationErrors = (event: ErrorEvent) => {
      if (
        event.error &&
        (event.error.message?.includes("removeChild") ||
          event.error.message?.includes("NotFoundError"))
      ) {
        console.warn(
          "Google Translate interference detected, suppressing error",
        );
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener("error", handleMutationErrors);

    return () => {
      window.removeEventListener("error", handleMutationErrors);
    };
  }, []);

  const fetchStudents = useCallback(async () => {
    if (!hasValidClassId) return;

    // Find the current class to get its year. Read via ref, not the
    // `classes` value directly: useAllClasses() re-fetches and hands back
    // a new array reference on every mount (even with a warm cache), and
    // depending on `classes` here would regenerate this callback and
    // double every dispatch below each time that happens.
    const currentClass = classesRef.current.find((c) => c.id === classId);
    const currentSchoolYear = getCurrentSchoolYear();

    if (currentClass && currentClass.year < currentSchoolYear) {
      // For past year classes, use only the historical API
      dispatch(
        getStudentsBeenInClass({
          classId: classId!,
        }),
      );
    } else if (currentClass && currentClass.year > currentSchoolYear) {
      // For future year classes, use only the normal API
      dispatch(
        getStudents({
          classId: classId!,
          sortValue: sortBy,
        }),
      );
    } else {
      // For current year classes, fetch from both APIs and merge
      try {
        const [currentStudentsAction, historicalStudentsAction] =
          await Promise.all([
            dispatch(
              getStudents({
                classId: classId!,
                sortValue: sortBy,
              }),
            ),
            dispatch(
              getStudentsBeenInClass({
                classId: classId!,
              }),
            ),
          ]);

        // Check if both API calls were successful
        if (
          getStudents.fulfilled.match(currentStudentsAction) &&
          getStudentsBeenInClass.fulfilled.match(historicalStudentsAction)
        ) {
          const currentStudents = currentStudentsAction.payload.students;
          const historicalStudents = historicalStudentsAction.payload.students;

          // Merge students, avoiding duplicates (prioritize current students)
          const mergedStudents = [...currentStudents];
          const currentStudentIds = new Set(currentStudents.map((s) => s.id));

          // Add historical students that aren't already in current students
          for (const historicalStudent of historicalStudents) {
            if (!currentStudentIds.has(historicalStudent.id)) {
              mergedStudents.push(historicalStudent);
            }
          }

          // Update the students state directly with merged results
          dispatch(setStudents({ students: mergedStudents }));
        }
      } catch (error) {
        console.error("Error fetching students from both APIs:", error);
        // Fallback to just current students
        dispatch(
          getStudents({
            classId: classId!,
            sortValue: sortBy,
          }),
        );
      }
    }
  }, [classId, hasValidClassId, dispatch, sortBy]);

  const fetchTrainingPictureCount = useCallback(
    async (studentId: number, signal?: AbortSignal): Promise<number> => {
      try {
        const pictures = await getTrainingPictures(
          studentId.toString(),
          signal,
        );
        if (signal?.aborted) return 0;
        return pictures.filter((picture) => picture.is_usable).length;
      } catch (error) {
        if (signal?.aborted) return 0;
        console.warn(
          `Failed to fetch training pictures for student ${studentId}:`,
          error,
        );
        return 0;
      }
    },
    [],
  );

  const fetchAllTrainingPictureCounts = useCallback(
    async (studentList: Student[]) => {
      if (studentList.length === 0) {
        setTrainingPictureCounts({});
        return;
      }

      // Only fetch counts we don't already have. Redux replaces `students`
      // with a new array reference on any single-student update (e.g. the
      // per-row auto profile-picture assignment in StudentInfo), which
      // re-runs this effect for the whole class — without this guard that
      // turned into a full re-fetch of every student's count per row.
      const knownCounts = trainingPictureCountsRef.current;
      const studentsNeedingCounts = studentList.filter(
        (student) => !(student.id in knownCounts),
      );
      if (studentsNeedingCounts.length === 0) return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setIsLoadingCounts(true);
      setCountsFetchError(null);

      try {
        const counts: TrainingPictureCounts = {};

        const batchSize = 5;
        for (let i = 0; i < studentsNeedingCounts.length; i += batchSize) {
          if (abortController.signal.aborted) break;

          const batch = studentsNeedingCounts.slice(i, i + batchSize);
          const batchPromises = batch.map((student) =>
            fetchTrainingPictureCount(student.id, abortController.signal).then(
              (count) => ({ studentId: student.id, count }),
            ),
          );

          const batchResults = await Promise.all(batchPromises);

          for (const { studentId, count } of batchResults) {
            counts[studentId] = count;
          }
        }

        if (!abortController.signal.aborted) {
          setTrainingPictureCounts((prev) => ({ ...prev, ...counts }));
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error("Failed to fetch training picture counts:", error);
          setCountsFetchError("Failed to load training picture counts");
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingCounts(false);
        }
      }
    },
    [fetchTrainingPictureCount],
  );

  useEffect(() => {
    // Wait for useAllClasses() to finish its initial load first: classes
    // starts empty and populates asynchronously, and fetchStudents needs it
    // to pick the right API (past/future/current year). Firing before it's
    // ready would fetch once with an empty `classes`, then fetchStudents'
    // identity would change and fetch again once `classes` arrives —
    // doubling every student/training-picture request below.
    if (hasValidClassId && !isLoadingClasses) {
      fetchStudents();
    }
  }, [fetchStudents, hasValidClassId, isLoadingClasses]);

  useEffect(() => {
    trainingPictureCountsRef.current = trainingPictureCounts;
  }, [trainingPictureCounts]);

  useEffect(() => {
    fetchAllTrainingPictureCounts(students);
  }, [students, fetchAllTrainingPictureCounts]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const renderDeliveryNg = useCallback((student: Student) => {
    try {
      return student.is_ng ? DELIVERY_NG_INDICATOR : null;
    } catch (error) {
      console.warn("Error rendering delivery NG:", error);
      return null;
    }
  }, []);

  const renderTrainingPictureCount = useCallback(
    (student: Student) => {
      try {
        if (student.is_ng) return null;

        const count = trainingPictureCounts[student.id];

        if (isLoadingCounts) {
          return <span className="text-gray-400">...</span>;
        }

        if (countsFetchError) {
          return (
            <span className="text-gray-400" title={countsFetchError}>
              -
            </span>
          );
        }

        if (count > 0) {
          return <span>{count}枚</span>;
        }

        return ERROR_INDICATOR;
      } catch (error) {
        console.warn("Error rendering training picture count:", error);
        return <span className="text-gray-400">-</span>;
      }
    },
    [trainingPictureCounts, isLoadingCounts, countsFetchError],
  );

  const renderParentsRegisterDate = useCallback((student: Student) => {
    try {
      if (!student.parent?.create_time) return null;

      return formatDate(new Date(student.parent.create_time), {
        [SupportedLang.ja]: "yo Mo do",
        [SupportedLang.en]: "MMMM, d, yyyy",
      });
    } catch (error) {
      console.warn("Invalid date format:", student.parent?.create_time);
      return <span className="text-gray-400">-</span>;
    }
  }, []);

  const renderHasParentRegistered = useCallback((student: Student) => {
    try {
      if (student.is_ng) return null;
      return student.parent ? SUCCESS_INDICATOR : ERROR_INDICATOR;
    } catch (error) {
      console.warn("Error rendering parent registration:", error);
      return null;
    }
  }, []);

  const renderPlanDisplay = useCallback(
    (planKey: string | null, fallbackPlan?: string) => {
      try {
        const key = planKey || fallbackPlan || "Free";
        const config = PLAN_CONFIGS[key];

        if (!config) return null;

        return (
          <span style={{ color: config.color }} title={config.text}>
            {config.text}
          </span>
        );
      } catch (error) {
        console.warn("Error rendering plan display:", error);
        return null;
      }
    },
    [],
  );

  const renderThisMonthPlan = useCallback(
    (student: Student) => {
      try {
        if (!facility || !student.parent) return null;

        if (facility.active_plan === "free_trial") {
          return renderPlanDisplay("free_trial");
        }

        if (facility.active_plan === "under_contract") {
          return renderPlanDisplay(student.parent.accessible_plan, "Free");
        }

        return null;
      } catch (error) {
        console.warn("Error rendering this month plan:", error);
        return null;
      }
    },
    [facility, renderPlanDisplay],
  );

  const renderNextMonthPlan = useCallback(
    (student: Student) => {
      try {
        if (!facility || !student.parent) return null;

        if (facility.next_month_plan === "free_trial") {
          return renderPlanDisplay("free_trial");
        }

        if (facility.next_month_plan === "under_contract") {
          const planName = student.parent.plan_id?.name;
          return renderPlanDisplay(planName, "Free");
        }

        return null;
      } catch (error) {
        console.warn("Error rendering next month plan:", error);
        return null;
      }
    },
    [facility, renderPlanDisplay],
  );

  const shouldGrayOutStudent = useCallback(
    (student: Student) => {
      try {
        // Find the current class
        const currentClass = classes.find((c) => c.id === classId);
        if (!currentClass) return false;

        const currentSchoolYear = getCurrentSchoolYear();

        // For past and current year classes: gray out students who have been moved to different classes
        // For future year classes: no graying out needed (regular API only, all students should be assigned)
        if (currentClass.year <= currentSchoolYear) {
          return student.class_id !== classId;
        }

        return false;
      } catch (error) {
        console.warn(
          "Error determining if student should be grayed out:",
          error,
        );
        return false;
      }
    },
    [classes, classId],
  );

  const renderStatusIndicator = useCallback(
    (isGraduated: boolean, isAdvanced: boolean) => {
      if (isGraduated) {
        return (
          <span className="ml-3 inline-flex items-center whitespace-nowrap rounded-full border border-red-300 bg-red-50 px-2 py-1 text-xs font-semibold text-red-600">
            卒園
          </span>
        );
      }

      if (isAdvanced) {
        return (
          <span className="ml-3 inline-flex items-center whitespace-nowrap rounded-full border border-red-300 bg-red-50 px-2 py-1 text-xs font-semibold text-red-600">
            進級
          </span>
        );
      }

      return null;
    },
    [],
  );

  if (!hasValidClassId) {
    return (
      <div className="flex-1">
        <Container>
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500">{t("_generic._invalid_class_id")}</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0" ref={containerRef} translate="no">
      <Container>
        {loading && <LoadingDialog />}
        <div className="h-full min-h-0 flex flex-col space-y-1">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-2">
            <div className="flex flex-col gap-2 w-full md:w-auto">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="whitespace-nowrap text-base font-medium">
                  {t("_student._total_student")}:{" "}
                  <span className="font-semibold">{students.length}</span>
                </p>
                <div className="w-full md:w-auto">
                  <Menu />
                </div>
              </div>
            </div>
            <div className="w-full md:w-auto flex justify-end">
              <div className="w-full md:w-auto">
                <SortDropdown />
              </div>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-auto relative">
            <div className="w-full min-h-full">
              {students.length === 0 && (
                <div className="h-full flex items-center justify-center">
                  <p className="text-gray-500">{t("_generic._no_data")}</p>
                </div>
              )}

              {students.length > 0 && (
                <div className="w-full">
                  <table className="min-w-[900px] w-full relative" role="table">
                    <thead>
                      <tr>
                        <th className="sticky top-0 z-10 p-2 min-w-64 bg-[#f7f7f7] border border-[#D9D9D9]">
                          <div className="flex justify-center items-center">
                            <p className="text-center">
                              {t("_student._student_name")}
                            </p>
                          </div>
                        </th>
                        <th className="sticky top-0 z-10 p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[120px] text-center">
                          {t("_student._delivery")}
                        </th>
                        <th className="sticky top-0 z-10 p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[120px] text-center">
                          {t("_student._number_of_face_photos")}
                        </th>
                        <th className="sticky top-0 z-10 p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[120px] text-center">
                          {t("_student._is_parent_registered")}
                        </th>
                        <th className="sticky top-0 z-10 p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[180px] text-center">
                          {t("_student._parents_registration_date")}
                        </th>
                        <th className="sticky top-0 z-10 p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[140px] text-center">
                          {t("_student._this_month_plan")}
                        </th>
                        <th className="sticky top-0 z-10 p-2 bg-[#f7f7f7] border border-[#D9D9D9] min-w-[140px] text-center">
                          {t("_student._next_month_plan")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student, index) => {
                        // Skip any malformed student objects
                        if (!student || !student.id) {
                          console.warn(
                            "Skipping malformed student at index:",
                            index,
                            student,
                          );
                          return null;
                        }

                        const isGraduated = isStudentGraduatedUtil(student);
                        const shouldGrayOut = shouldGrayOutStudent(student);
                        const isAdvanced = !isGraduated && shouldGrayOut;

                        // Determine row styling with precedence: graduated > moved classes
                        let rowClassName = "";
                        if (isGraduated) {
                          rowClassName = "bg-gray-300";
                        } else if (shouldGrayOut) {
                          rowClassName = "bg-gray-200 text-gray-500";
                        }

                        return (
                          <tr key={student.id} className={rowClassName}>
                            <td className="p-2 border border-[#D9D9D9]">
                              <div className="flex items-start justify-between">
                                <StudentInfo student={student} />
                                {renderStatusIndicator(isGraduated, isAdvanced)}
                              </div>
                            </td>
                            <td className="p-2 border border-[#D9D9D9] text-center">
                              {renderDeliveryNg(student)}
                            </td>
                            <td className="p-2 border border-[#D9D9D9] text-center">
                              {renderTrainingPictureCount(student)}
                            </td>
                            <td className="p-2 border border-[#D9D9D9] text-center">
                              {renderHasParentRegistered(student)}
                            </td>
                            <td className="p-2 border border-[#D9D9D9] text-center">
                              {renderParentsRegisterDate(student)}
                            </td>
                            <td className="p-2 border border-[#D9D9D9] text-center">
                              {renderThisMonthPlan(student)}
                            </td>
                            <td className="p-2 border border-[#D9D9D9] text-center">
                              {renderNextMonthPlan(student)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default StudentTable;
